import { useField } from 'formik';
import { Reducer, useMemo, useReducer } from 'react';
import { PreviewUserDataFragment, UserTagOptionDataFragment } from '../../../../../api';
import { DealerStatus, SelectedDealer } from '../types';
import {
    createSyncReducer,
    BuildSelectedDealerIds,
    getSelectedDealerIds,
    SelectionAction,
    SelectionActionType,
    SelectionState,
} from './selectionReducer';

type UseInviteQuoteSelectionProps = {
    dealers: PreviewUserDataFragment[];
    tags: UserTagOptionDataFragment[];
    inSessionDealerIds?: string[];
    inSessionTagIds?: string[];
    ignoreInSessionFilter?: boolean;
};

type SelectionData = {
    selectedDealers: SelectedDealer[];
    taggedDealerIds: Set<string>;
};

const useInviteQuoteSelection = ({
    dealers,
    tags,
    inSessionTagIds,
    inSessionDealerIds,
    ignoreInSessionFilter,
}: UseInviteQuoteSelectionProps) => {
    const [, { initialValue: initialDealerIds = [] }, { setValue: setDealerIds }] = useField<string[]>('dealerIds');
    const [
        ,
        ,
        { setValue: setSelectedDealerIds, setError: setSelectedDealerError, setTouched: setSelectedDealerTouched },
    ] = useField<string[]>('selectedDealerIds');
    const [, { initialValue: initialTagIds }, { setValue: setTagIds }] = useField<string[]>('tagIds');

    const buildSelectedDealerIds = useMemo<BuildSelectedDealerIds>(
        () => state => getSelectedDealerIds(state, { ignoreInSessionFilter, inSessionDealerIds, tags }),
        [ignoreInSessionFilter, inSessionDealerIds, tags]
    );

    const syncReducer = useMemo(
        () => createSyncReducer({ setDealerIds, setSelectedDealerIds, setTagIds, buildSelectedDealerIds }),
        [setDealerIds, setSelectedDealerIds, setTagIds, buildSelectedDealerIds]
    );

    const [{ dealerIds, tagIds }, dispatch] = useReducer<Reducer<SelectionState, SelectionAction>>(syncReducer, {
        dealerIds: initialDealerIds,
        tagIds: initialTagIds,
    });

    const onTagSelectionChange = (nextTagIds: string[]) => {
        const addedTagDealerIds = nextTagIds
            .filter(tagId => !tagIds.includes(tagId))
            .flatMap(tagId => tags.find(tag => tag.id === tagId)?.userIds ?? []);

        dispatch({ type: SelectionActionType.SetTagIds, tagIds: nextTagIds, addedTagDealerIds });
    };

    const onDealerSelectionChange = (nextDealerIds: string[]) => {
        dispatch({ type: SelectionActionType.SetDealerIds, dealerIds: nextDealerIds });
    };

    const selectionData = useMemo<SelectionData>(() => {
        const taggedDealerNames = new Map<string, string[]>();

        tags.filter(tag => tagIds.includes(tag.id)).forEach(tag => {
            tag.userIds.forEach(userId => {
                const tagNames = taggedDealerNames.get(userId) ?? [];
                tagNames.push(tag.name);
                taggedDealerNames.set(userId, tagNames);
            });
        });

        const getStatus = (dealerId: string) =>
            (inSessionDealerIds ?? []).includes(dealerId) ? DealerStatus.InSession : DealerStatus.New;

        const selectedTaggedDealers: SelectedDealer[] = Array.from(taggedDealerNames.entries()).map(
            ([userId, tagNames]) => ({
                id: userId,
                name: dealers.find(dealer => dealer.id === userId)?.fullName ?? '',
                tagNames: tagNames.join(', '),
                status: getStatus(userId),
            })
        );

        const selectedIndividualDealers: SelectedDealer[] = dealers
            .filter(dealer => dealerIds.includes(dealer.id) && !taggedDealerNames.has(dealer.id))
            .map(dealer => ({
                id: dealer.id,
                name: dealer.fullName,
                tagNames: null,
                status: getStatus(dealer.id),
            }));

        return {
            selectedDealers: selectedTaggedDealers
                .concat(selectedIndividualDealers)
                .sort((a, b) => a.name.localeCompare(b.name)),
            taggedDealerIds: new Set(taggedDealerNames.keys()),
        };
    }, [dealers, dealerIds, inSessionDealerIds, tagIds, tags]);

    const availableDealers = useMemo(() => {
        const excludedDealerIds = new Set(selectionData.taggedDealerIds);

        if (!ignoreInSessionFilter) {
            (inSessionDealerIds ?? []).forEach(dealerId => excludedDealerIds.add(dealerId));
        }

        return dealers.filter(dealer => !excludedDealerIds.has(dealer.id));
    }, [dealers, ignoreInSessionFilter, inSessionDealerIds, selectionData.taggedDealerIds]);

    const availableTags = useMemo(() => {
        if (ignoreInSessionFilter) {
            return tags;
        }

        return tags.filter(tag => !(inSessionTagIds ?? []).includes(tag.id));
    }, [ignoreInSessionFilter, inSessionTagIds, tags]);

    const resetSelection = () => {
        dispatch({ type: SelectionActionType.Reset });
        setSelectedDealerError(undefined);
        setSelectedDealerTouched(false, false);
    };

    return {
        availableTags,
        availableDealers,
        onDealerSelectionChange,
        onTagSelectionChange,
        selectedDealers: selectionData.selectedDealers,
        resetSelection,
    };
};

export default useInviteQuoteSelection;
